import FatalErrorPage from '../components/FatalErrorPage/FatalErrorPage';

/**
 * Wrap a lazy `import()` so a failed chunk load recovers gracefully.
 *
 * A dynamic import can reject for transient reasons — most commonly a stale
 * `index.html` pointing at hashed chunk names that a new deploy removed, or a
 * Vite "Outdated Optimize Dep" 504 in dev. A one-shot reload fixes those.
 *
 * The reload is BOUNDED: we reload at most once per failing load. If the import
 * still fails right after a reload, the problem is not transient, so we stop
 * reloading and render FatalErrorPage instead of refreshing forever. Without
 * this guard a persistent chunk failure turns into an infinite refresh loop
 * where the page never loads. On success we clear the marker so a genuinely
 * new failure (e.g. the next deploy) can trigger a fresh one-shot reload.
 */
const RELOAD_MARKER = 'chunk-reload-attempted-at';
// If a failure lands within this window of the last reload, treat it as a loop
// rather than a fresh transient failure and stop reloading.
const RELOAD_GUARD_MS = 10_000;

function readLastReloadAt(): number {
  try {
    return Number(window.sessionStorage.getItem(RELOAD_MARKER)) || 0;
  } catch {
    return 0;
  }
}

function writeLastReloadAt(value: number): void {
  try {
    if (value === 0) {
      window.sessionStorage.removeItem(RELOAD_MARKER);
    } else {
      window.sessionStorage.setItem(RELOAD_MARKER, String(value));
    }
  } catch {
    // sessionStorage can be unavailable (private mode / blocked storage).
    // The reload itself still happens; we just can't guard as tightly.
  }
}

export default function reloadOnFail(
  fn: () => Promise<{ default: React.ComponentType<any> }>,
): Promise<{ default: React.ComponentType<any> }> {
  return new Promise((resolve) => {
    fn()
      .then((module) => {
        // Loaded cleanly — allow a future failure to reload once again.
        writeLastReloadAt(0);
        resolve(module);
      })
      .catch(() => {
        const now = Date.now();
        const reloadedRecently = now - readLastReloadAt() < RELOAD_GUARD_MS;

        if (reloadedRecently) {
          // We already reloaded and it still failed — reloading again would
          // loop forever. Surface the error instead (its Refresh button clears
          // caches, which a bare reload does not).
          resolve({ default: FatalErrorPage });
          return;
        }

        writeLastReloadAt(now);
        window.location.reload();
        // Keep something renderable until the reload takes over.
        resolve({ default: FatalErrorPage });
      });
  });
}
